import { FlowContext } from 'src/lib/flow';
import { FlowPage } from 'src/lib/flow/FlowPage';
import { FlowQuestion } from 'src/lib/flow/FlowQuestion';
import { hasClient, yourHome } from 'src/lib/flow/text';

type EligibilityVisiblityFlags = {
  showUniqueOwnershipStructure: boolean;
  showUndergroundOilTank: boolean;
  showBelowMarketRate: boolean;
  showRentControlledTenantOccupied: boolean;
  showCesspool: boolean;
};

// used in exclusives eligibilityCriteriaPage
export const getEligibilityFlags = (market: string): EligibilityVisiblityFlags => {
  return {
    showUniqueOwnershipStructure: market === 'sf_bay_area' || market === 'new_york_new_jersey',
    showUndergroundOilTank: market === 'new_york_new_jersey',
    showBelowMarketRate: market === 'sf_bay_area',
    showRentControlledTenantOccupied: market === 'sf_bay_area',
    showCesspool: market === 'boston_ma' || market === 'new_york_new_jersey',
  };
};

function title({ answers, inputContext: { channel } }: FlowContext): string {
  return `Do any of these apply to ${yourHome(answers, channel)}?`;
}

const eligibilityCriteriaPage: FlowPage = {
  slug: () => 'eligibility-criteria',
  isEnabled: () => true,
  autoAdvance: () => true,
  title,
  subtitle: () =>
    'Select all that apply. We keep an eye out for these things when we’re making an offer.',
  questions: ({ answers, inputContext: { channel, market } }): FlowQuestion[] => {
    const {
      showUniqueOwnershipStructure,
      showUndergroundOilTank,
      showBelowMarketRate,
      showRentControlledTenantOccupied,
      showCesspool,
    } = getEligibilityFlags(market);

    return [
      {
        key: 'home.eligibility_criteria',
        type: 'multi_select',
        showNoneOfTheAbove: true,
        options: [
          {
            label: 'Leased or financed solar panels',
            value: 'leased_solar_panels',
            followUpInfo:
              'You may need to buy out the lease or remove the panels to sell to us. We can talk through this later.',
          },
          {
            label: 'Known foundation issues',
            value: 'known_foundation_issues',
            description: 'Excessive cracking, uneven floors',
          },
          {
            label: 'Fire damage',
            value: 'fire_damage',
          },
          {
            label: 'Well water',
            value: 'well_water',
            description: `${
              hasClient(answers, channel) ? 'Your client maintains' : 'You maintain'
            } a well to supply water`,
          },
          {
            label: 'Septic system',
            value: 'septic',
            description: 'Separate from municipal sewage',
          },

          {
            label: 'Asbestos siding',
            value: 'asbestos_siding',
          },
          {
            label: 'Horse property',
            value: 'livestock',
            description: 'Livestock live on property',
          },
          {
            label: 'Mobile or manufactured home',
            value: 'mobile_manufactured_home',
          },
          {
            label: 'Unique ownership structure',
            value: 'unique_ownership_structure',
            description: 'Tenants in Common, Co-Ops, etc',
            hidden: !showUniqueOwnershipStructure,
          },
          {
            label: 'Part of a Below Market Rate (BMR) Ownership Program',
            value: 'below_market_rate_ownership',
            hidden: !showBelowMarketRate,
          },
          {
            label: 'Rent controlled and has a tenant today',
            value: 'rent_controlled_tenant_occupied',
            hidden: !showRentControlledTenantOccupied,
          },
          {
            label: 'Existing active or inactive underground fuel oil tanks',
            value: 'underground_oil_tank',
            hidden: !showUndergroundOilTank,
          },
          {
            label: 'Cesspool on property',
            value: 'cesspool',
            hidden: !showCesspool,
          },
        ],
        optional: true,
      },
    ];
  },
};

export default eligibilityCriteriaPage;
